import { computed, reactive } from 'vue'
import { useStore } from '../store/index'

/**
 * Traducciones del encargo online (estilo gettext).
 * La clave es el propio texto en español; las traducciones están en data/solicitudWebTextos/<idioma>.json
 * y se cargan bajo demanda (solo el idioma de la web y el inglés de reserva).
 * Si falta una traducción se ve en inglés, y si tampoco existe, en español.
 */

/** Idiomas que se escriben de derecha a izquierda. */
export const IDIOMAS_RTL = ['ar', 'he']

/** Idioma seleccionado en la web (el mismo que usa el resto de páginas). */
export const idiomaSolicitud = computed<string>(() => {
  try {
    const s = useStore()
    return String(s.idioma || s.NOTARIA?.Languaje || 'es').toLowerCase().slice(0, 2)
  } catch {
    return 'es'
  }
})

const ficheros = import.meta.glob<Record<string, string>>('../data/solicitudWebTextos/*.json', { import: 'default' })
const TEXTOS = reactive<Record<string, Record<string, string>>>({})
const pedidos = new Set<string>()

function asegurar(idioma: string) {
  if (idioma === 'es' || pedidos.has(idioma)) return
  pedidos.add(idioma)
  const cargar = ficheros[`../data/solicitudWebTextos/${idioma}.json`]
  if (!cargar) return
  cargar()
    .then((t) => { TEXTOS[idioma] = t })
    .catch((e) => { pedidos.delete(idioma); console.warn('[SolicitudWeb] No se pudieron cargar los textos', idioma, e) })
}

/** true cuando ya están cargados los textos del idioma de la web (para no mostrar el formulario a medio traducir). */
export const textosListos = computed(() => {
  const idioma = idiomaSolicitud.value
  if (idioma === 'es' || !ficheros[`../data/solicitudWebTextos/${idioma}.json`]) return true
  asegurar(idioma)
  return !!TEXTOS[idioma]
})

/** Precarga los textos del idioma actual (opcional: t() también los pide al usarse). */
export function cargarTextosSolicitud() {
  asegurar(idiomaSolicitud.value)
  asegurar('en')
}

export function t(clave: string, vars?: Record<string, string | number>): string {
  const idioma = idiomaSolicitud.value
  let texto = clave
  if (idioma !== 'es') {
    asegurar(idioma)
    asegurar('en')
    texto = TEXTOS[idioma]?.[clave] ?? TEXTOS.en?.[clave] ?? clave
  }
  if (vars) texto = texto.replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined ? String(vars[k]) : m))
  return texto
}

/**
 * Traduce a un idioma concreto, distinto del de la web (la pantalla QR en modo sala de espera
 * va rotando el título por los idiomas de la notaría). Pide el fichero de ese idioma si hace falta.
 */
export function tIdioma(idioma: string, clave: string, vars?: Record<string, string | number>): string {
  const i = (idioma || 'es').toLowerCase().slice(0, 2)
  let texto = clave
  if (i !== 'es') {
    asegurar(i)
    texto = TEXTOS[i]?.[clave] ?? clave
  }
  if (vars) texto = texto.replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined ? String(vars[k]) : m))
  return texto
}
